import {
	faPaperPlane,
	faPhone,
	faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Multiselect } from "multiselect-react-dropdown";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const LEN_OF_TEXT = 200;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Styles
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "30px" };
const DASHBOARD_ROW_STYLE = {
	paddingTop: "20px",
	paddingBottom: "20px",
	backgroundColor: "#A7D588",
};
const STATS_CARD_STYLE = { backgroundColor: "#24A2B3" };
const TABLE_CONTAINER_STYLE = { padding: "10px" };
const VIEW_BUTTON_STYLE = {
	backgroundColor: "#00ccff",
	border: "none",
	borderRadius: "4px",
	margin: "5px",
	color: "#fff",
};
const ACTION_CELL_STYLE = {
	fontSize: "20px",
	textAlign: "center",
	fontFamily: "Roboto, sans-serif",
};

const Superadmin = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();

	// Modal state
	const [show, setShow] = useState(false);

	// Form state
	const [name, setName] = useState("");
	const [number, setNumber] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [dropdown, setDropdown] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [errors, setErrors] = useState({});

	// Data state
	const [coursesArray, setCoursesArray] = useState([]);
	const [instructors, setInstructors] = useState([]);
	const [loading, setLoading] = useState(false);

	// Modal handlers
	const handleClose = useCallback(() => {
		setShow(false);
		setName("");
		setNumber("");
		setEmail("");
		setPassword("");
		setErrors({});
		setDropdown([]);
		setShowPassword(false);
	}, []);

	const handleShow = useCallback(() => setShow(true), []);

	// Form validation
	const validateForm = useCallback(() => {
		const newErrors = {};

		if (!name) {
			newErrors.name = "Name is required.";
		} else if (name.length > LEN_OF_TEXT) {
			newErrors.name = "Name should not exceed character limit.";
		}

		if (!email) {
			newErrors.email = "Email is required.";
		} else if (!EMAIL_REGEX.test(email)) {
			newErrors.emailInvalid = "Email is invalid.";
		}

		if (!number) {
			newErrors.number = "Phone number is required.";
		} else if (number.length < 10 || number.length > 15) {
			newErrors.number = "Phone number should be between 10 to 15 digits.";
		}

		if (!password) {
			newErrors.password = "Password is required.";
		} else if (!PASSWORD_REGEX.test(password)) {
			newErrors.passwordInvalid =
				"Password should contain at least one uppercase, lowercase, number, special character, and be at least 8 characters long.";
		}

		if (dropdown.length === 0) {
			newErrors.courses = "Please select at least one course.";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [name, email, number, password, dropdown]);

	// Form submission handler
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();

			if (!validateForm()) {
				return;
			}

			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI("createinstructors", {
					name,
					mobile: number,
					email,
					courses_approved: JSON.stringify(dropdown),
					password,
				});

				if (res.status === 200) {
					alert("Instructor added successfully.");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error creating instructor:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: err.response?.data?.msg || "An error occurred" },
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[name, number, email, password, dropdown, validateForm, dispatch]
	);

	// Fetch courses
	useEffect(() => {
		const fetchCourses = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
				const res = await getDataAPI(
					`getAllCoursesNames/${auth.user.id}/9ae8b71c-dd3f-4fad-b1ee-e872f951e7af`,
					auth.token
				);
				setCoursesArray(res.data.courses || []);
			} catch (err) {
				console.error("Error fetching courses:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch courses" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchCourses();
	}, [auth.token, auth.user?.id, dispatch]);

	// Fetch instructors
	useEffect(() => {
		const fetchInstructors = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
				const res = await getDataAPI("/getAllInstructors", auth.token);
				setInstructors(res.data.instructors || []);
			} catch (err) {
				console.error("Error fetching instructors:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch instructors" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchInstructors();
	}, [auth.token, dispatch]);

	// Multiselect handlers
	const onSelect = useCallback((option) => {
		setDropdown(option);
	}, []);

	const onRemove = useCallback((option) => {
		setDropdown(option);
	}, []);

	// Memoized components
	const instructorRows = useMemo(() => {
		return instructors.map((instructor, index) => {
			const coursesApproved = instructor.courses_approved
				? JSON.parse(instructor.courses_approved)
				: [];

			const coursesDisplay = coursesApproved
				.map((course, idx) => {
					if (idx === 0) return course.coursetitle;
					if (idx === coursesApproved.length - 1)
						return course.coursetitle + ".";
					return course.coursetitle + ", ";
				})
				.join("");

			return (
				<tr key={instructor.id}>
					<td>{index + 1}</td>
					<td>{instructor.name}</td>
					<td>{instructor.email}</td>
					<td>{instructor.mobile}</td>
					<td>{coursesDisplay}</td>
					<td style={ACTION_CELL_STYLE}>
						<Button
							onClick={() => {
								window.location.href = `/instructorinfo/${instructor.id}`;
							}}
							style={VIEW_BUTTON_STYLE}>
							View
						</Button>
					</td>
				</tr>
			);
		});
	}, [instructors]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Modal show={show} onHide={handleClose}>
				<div className="js">
					<div className="x-style" onClick={handleClose}>
						<span style={{ cursor: "pointer" }}>X</span>
					</div>
					<div className="js-boxIn">
						<div className="js-boxIn-content">
							<div className="ai-greenbox">
								<div>
									<h4 className="js-greenbox-heading">Add Instructor</h4>
								</div>
								<div>
									<form onSubmit={handleSubmit}>
										<div className="js-inputbox">
											<span className="js-inputbox-input">
												<input
													type="text"
													placeholder="Full Name"
													value={name}
													onChange={(e) => setName(e.target.value)}
													maxLength={LEN_OF_TEXT}
												/>
											</span>
											<div className="js-Icon">
												<FontAwesomeIcon icon={faUser} />
											</div>
										</div>
										<p
											style={{
												visibility: errors.name ? "visible" : "hidden",
												color: "red",
											}}
											className="error">
											{errors.name || "Name is required."}
										</p>

										<div className="js-inputbox">
											<span className="js-inputbox-input">
												<input
													type="email"
													placeholder="Email"
													value={email}
													onChange={(e) => setEmail(e.target.value)}
													maxLength={LEN_OF_TEXT}
												/>
											</span>
											<div className="js-Icon">
												<FontAwesomeIcon icon={faPaperPlane} />
											</div>
										</div>
										<p
											style={{
												visibility:
													errors.email || errors.emailInvalid
														? "visible"
														: "hidden",
												color: "red",
											}}
											className="error">
											{errors.email ||
												errors.emailInvalid ||
												"Email is required."}
										</p>

										<div className="js-inputbox">
											<span className="js-inputbox-input">
												<PhoneInput
													country={"in"}
													value={number}
													onChange={setNumber}
													inputStyle={{ width: "100%" }}
												/>
											</span>
											<div className="js-Icon">
												<FontAwesomeIcon icon={faPhone} />
											</div>
										</div>
										<p
											style={{
												visibility: errors.number ? "visible" : "hidden",
												color: "red",
											}}
											className="error">
											{errors.number || "Phone number is required."}
										</p>

										<div className="js-inputbox">
											<span className="js-inputbox-input">
												<Multiselect
													options={coursesArray}
													selectedValues={dropdown}
													onSelect={onSelect}
													onRemove={onRemove}
													displayValue="coursetitle"
													isObject={true}
												/>
											</span>
										</div>
										<p
											style={{
												visibility: errors.courses ? "visible" : "hidden",
												color: "red",
											}}
											className="error">
											{errors.courses || "Select course"}
										</p>

										<div className="js-inputbox">
											<span className="js-inputbox-input">
												<input
													type={showPassword ? "text" : "password"}
													placeholder="Password"
													value={password}
													autoComplete="off"
													onChange={(e) => setPassword(e.target.value)}
													maxLength={LEN_OF_TEXT}
												/>
											</span>
											<span
												className="toggle-password d-flex align-items-center"
												onClick={() => setShowPassword(!showPassword)}>
												{showPassword ? (
													<FaEyeSlash className="me-1" />
												) : (
													<FaEye className="me-1" />
												)}
											</span>
										</div>

										<p
											className="error-message ms-3"
											style={{
												visibility:
													errors.password || errors.passwordInvalid
														? "visible"
														: "hidden",
											}}>
											{errors.password ||
												errors.passwordInvalid ||
												"Password is required."}
										</p>

										<button
											type="submit"
											className="js-submitbtn"
											disabled={loading}>
											{loading ? "SUBMITTING..." : "SUBMIT"}
										</button>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Modal>

			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="button-format">
					<div className="dashboard-text">Super Admin Dashboard</div>
					<button className="addinstructor-btn" onClick={handleShow}>
						+ ADD INSTRUCTOR
					</button>
				</div>

				<div>
					<div className="row format">
						<Sidenavbar />
						<div className="col-8">
							<div className="row format" style={DASHBOARD_ROW_STYLE}>
								<div className="col-4 text-format" style={STATS_CARD_STYLE}>
									<div className="text1">{instructors.length}</div>
									<div className="text1">Instructors</div>
								</div>
							</div>

							<div className="row mt-4" style={TABLE_CONTAINER_STYLE}>
								<div className="col">
									<h4 className="instructorlist-text">Instructors List</h4>
									<Table>
										<thead id="pl">
											<tr>
												<th>S.No</th>
												<th>Name</th>
												<th>Email Address</th>
												<th>Phone No</th>
												<th>Courses Approved</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>{instructorRows}</tbody>
									</Table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default Superadmin;
